import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { API_URL } from '@/lib/config';
import { Document, Flow, Block } from '../types/Document';

// Import hooks
import { useDocumentData } from '../hooks/useDocumentData';
import { useDocumentProcessing } from '../hooks/useDocumentProcessing';
import { useProcessingResults } from '../hooks/useProcessingResults';
import { useSiteflowIntegration } from '../hooks/useSiteflowIntegration';

// Import components
import DocumentHeader from './document/DocumentHeader';
import DocumentPreviewPane from './document/DocumentPreviewPane';
import ProcessingResultsPane from './document/ProcessingResultsPane';
import ProcessingResultsHeader from './document/ProcessingResultsHeader';
import WorkflowResultsPane from './document/WorkflowResultsPane';

// Import utils
import {
  formatDisplayDocumentType,
  getLayoutType,
  renderHtml,
  renderHtmlContent,
  hasHtmlContent,
} from '../lib/document-utils';
import { updateDocumentProcessingResults } from '@/lib/api-client';

export default function DocumentView() {
  // --- React Router and Auth Hooks (Top Level) ---
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // --- Resizing State & Refs (Top Level) ---
  const [previewHeight, setPreviewHeight] = useState<number>(400);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const dragStartRef = useRef<{ initialY: number; initialHeight: number } | null>(null);

  // --- Custom Application Hooks (Top Level) ---
  const resultsHook = useProcessingResults();
  const documentDataHook = useDocumentData(id ? resultsHook.fetchProcessingResults : async () => {});
  const processingHook = useDocumentProcessing();
  const siteflowHook = useSiteflowIntegration();

  // --- Destructure from Custom Hooks (after they are all called) ---
  const {
    document,
    loading: docLoading,
    error: docError,
    editingTitle,
    newTitle,
    setNewTitle,
    savingTitle,
    handleSaveTitle,
    cancelTitleEdit,
    setEditingTitle,
    fetchDocument
  } = documentDataHook;

  const {
    processing,
    message: processingMessage,
    error: processingError,
    handleProcess,
    clearMessages: clearProcessingMessages
  } = processingHook;

  const {
    processingResults,
    editableResults,
    loadingResults,
    editMode,
    savingEdits,
    message: resultsMessage,
    error: resultsError,
    processingWarnings,
    collapsedPhases,
    openMenuPath,
    setOpenMenuPath,
    setEditableResultsState,
    toggleEditMode,
    cancelEditing,
    saveEditedResults,
    handleInputChange,
    togglePhaseCollapse,
    handleDeleteItem,
    handleAddItem,
    handleChangeItemType,
    handleCreateStepFromSelection,
    clearMessages: clearResultsMessages,
    isFlowArray,
    hasStructuredResults
  } = resultsHook;

  const {
    sendingToSiteflow,
    message: siteflowMessage,
    error: siteflowError,
    handleSendToSiteflow,
    clearMessages: clearSiteflowMessages
  } = siteflowHook;

  // --- Resize Handler Callbacks (Top Level) ---
  const currentLayoutType = document ? getLayoutType(document.documentType) : 'stacked';
  const documentTypeUpperCase = document ? document.documentType?.toUpperCase() : undefined;

  const handleMouseDownOnResizeHandle = useCallback((e: React.MouseEvent) => {
    if (currentLayoutType !== 'stacked' || documentTypeUpperCase !== 'DSI') return;
    e.preventDefault();
    setIsResizing(true);
    dragStartRef.current = {
      initialY: e.clientY,
      initialHeight: previewHeight,
    };
  }, [previewHeight, currentLayoutType, documentTypeUpperCase]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !dragStartRef.current) return;
    const deltaY = e.clientY - dragStartRef.current.initialY;
    const newHeight = dragStartRef.current.initialHeight + deltaY;
    setPreviewHeight(Math.max(100, Math.min(newHeight, window.innerHeight * 0.8)));
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    dragStartRef.current = null;
  }, []);

  // --- useEffect Hooks (Top Level) ---
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);
  
  useEffect(() => {
    return () => {
      clearProcessingMessages();
      clearResultsMessages();
      clearSiteflowMessages();
    };
  }, [clearProcessingMessages, clearResultsMessages, clearSiteflowMessages]);

  // Removed large console.log to prevent UI freezing when DevTools is open

  // --- Early returns for loading/error states (AFTER ALL HOOKS) ---
  if (docLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Combine overallError check with document check as document is needed for rendering
  const overallError = docError || resultsError || processingError;
  if (overallError || !document) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
              <p className="mt-1 text-sm text-gray-500">{overallError || 'Document not found or failed to load'}</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Derived State (after document is confirmed to exist) ---
  const resultsToDisplay = editMode ? editableResults : processingResults;
  const displayMessage = resultsMessage || processingMessage || siteflowMessage;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {displayMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 shadow-md fixed top-16 left-0 right-0 z-20" role="alert">
          <span className="block sm:inline">{displayMessage}</span>
        </div>
      )}
      {overallError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 shadow-md fixed top-16 left-0 right-0 z-20" role="alert">
          <span className="block sm:inline">{overallError}</span>
        </div>
      )}
      
      {/* Fixed header spacer */}
      <div className="h-16 flex-shrink-0"></div>
      
      {/* Message banner spacer - only when messages are shown */}
      {(displayMessage || overallError) && <div className="h-12 flex-shrink-0"></div>}
      
      <div className="flex-1 max-w-12xl mx-auto px-4 sm:px-6 lg:px-8 w-full min-h-0 py-4"> 
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
          <DocumentHeader 
            document={document}
            editingTitle={editingTitle}
            newTitle={newTitle}
            setNewTitle={setNewTitle}
            savingTitle={savingTitle}
            handleSaveTitle={handleSaveTitle}
            cancelTitleEdit={cancelTitleEdit}
            setEditingTitle={setEditingTitle}
            isProcessing={processing} 
            handleProcessAction={() => { 
              if (id && document) { 
                handleProcess(document, id, fetchDocument);
              }
            }}
            id={id}
            processingResults={processingResults}
          />

          {/* For the new workflow editor UI, we manage its own header, so we skip the external header when DSI */}

          <div className="flex-1 p-6 overflow-hidden min-h-0">
            <div className={`flex h-full min-h-0 ${currentLayoutType === 'stacked' ? 'flex-col' : 'flex-col md:flex-row'}`}>
              <DocumentPreviewPane
                document={document}
                userToken={user?.token}
                currentLayoutType={currentLayoutType}
                height={currentLayoutType === 'stacked' ? previewHeight : undefined}
              />
              {currentLayoutType === 'stacked' && (
                <div
                  onMouseDown={handleMouseDownOnResizeHandle}
                  className="w-full h-4 bg-transparent cursor-ns-resize flex items-center justify-center flex-shrink-0 relative py-1 my-1 group"
                  title="Resize preview"
                >
                  <div className="flex-grow h-px bg-gray-300 group-hover:bg-gray-400"></div>
                  <div className="mx-2 px-2 py-0.5 bg-gray-200 group-hover:bg-gray-300 rounded-md flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-600 group-hover:text-gray-700">
                      <circle cx="5" cy="12" r="1.5" fill="currentColor"/>
                      <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                      <circle cx="19" cy="12" r="1.5" fill="currentColor"/>
                    </svg>
                  </div>
                  <div className="flex-grow h-px bg-gray-300 group-hover:bg-gray-400"></div>
                </div>
              )}
              {document.processingStatus === 'done' && isFlowArray && Array.isArray(processingResults) ? (
                <div className={currentLayoutType === 'stacked' ? 'flex-1 min-h-0 overflow-hidden' : 'w-full md:w-1/2 h-full min-h-0 overflow-hidden'}>
                  <WorkflowResultsPane
                    initialResults={processingResults as any}
                    warnings={processingWarnings}
                    onSave={async (updated) => {
                      if (!id) return;
                      await updateDocumentProcessingResults(id, { status: 'done', result: updated });
                      // Don't refetch document - it resets the workflow context state including edit mode
                    }}
                    onSendToSiteflow={async (flows, projectId, familyId) => {
                      await handleSendToSiteflow(flows as any, true, projectId, familyId);
                    }}
                    sendingToSiteflow={sendingToSiteflow}
                    siteflowError={siteflowError}
                    siteflowMessage={siteflowMessage}
                  />
                </div>
              ) : (
                <ProcessingResultsPane
                  documentType={document.documentType}
                  processingStatus={document.processingStatus}
                  warnings={processingWarnings}
                  processedAt={document.processedAt}
                  currentLayoutType={currentLayoutType}
                  className={currentLayoutType === 'stacked' ? 'flex-1 min-h-0' : 'w-full md:w-1/2 h-full min-h-0'}
                  processingResults={processingResults}
                  editableResults={editableResults}
                  loadingResults={loadingResults}
                  editMode={editMode}
                  savingEdits={savingEdits}
                  collapsedPhases={collapsedPhases}
                  openMenuPath={openMenuPath}
                  setOpenMenuPath={setOpenMenuPath}
                  toggleEditMode={toggleEditMode}
                  cancelEditing={cancelEditing}
                  saveEditedResults={() => { id && saveEditedResults(id);}}
                  handleInputChange={handleInputChange}
                  togglePhaseCollapse={togglePhaseCollapse}
                  handleDeleteItem={handleDeleteItem}
                  handleAddItem={handleAddItem}
                  handleChangeItemType={handleChangeItemType}
                  handleCreateStepFromSelection={handleCreateStepFromSelection}
                  isFlowArray={isFlowArray}
                  hasStructuredResults={hasStructuredResults}
                  setEditableResultsState={setEditableResultsState}
                  sendingToSiteflow={sendingToSiteflow}
                  siteflowError={siteflowError}
                  siteflowMessage={siteflowMessage}
                  handleSendToSiteflow={(projectId: string, familyId: string) => handleSendToSiteflow(processingResults, isFlowArray, projectId, familyId)}
                />
              )}
            </div>
          </div>
         </div>
       </div>
     </div>
   );
} 